import { MouseEventHandler, useContext, useMemo } from 'react';
import { useAdCampaignsDataContext } from 'src/contexts/AdCampaignsDataContext';
import { useAppContext } from 'src/contexts/AppContext';
import { useCatalogDataContext } from 'src/contexts/CatalogDataContext';
import { Content } from 'src/contexts/ContentContext';
import {
  EventMapContextProvider,
  useEventMapContextTicketClassesSelector,
} from 'src/contexts/EventMapContext';
import { ModalContext } from 'src/contexts/ModalContext';
import { vars } from 'src/core/themes';
import { Button, Stack } from 'src/core/ui';
import { useGetEventFullInfo } from 'src/hooks/useGetEventFullInfo';
import { useMatchMedia } from 'src/hooks/useMatchMedia';
import { useUserHasAnyOfPermissions } from 'src/hooks/useUserHasAnyOfPermissions';
import { CampaignModal } from 'src/modals/SponsoredListings/CampaignModal';
import { ChevronDownIconV2 } from 'src/svgs/Viagogo/goicons/ChevronDownIconV2';
import { ContentId } from 'src/utils/constants/contentId';
import { getIsSponsoredListingsEligibleEvent } from 'src/utils/eventWithDataUtils';
import {
  Event,
  Permission,
  SponsoredListingsCampaign,
  TicketClassInfo,
} from 'src/WebApiController';

import {
  BoostListingTabButton,
  StartCampaignTabButton,
} from '../BoostListingTabButton/BoostListingTabButton';
import * as tabButtonStyles from '../BoostListingTabButton/BoostListingTabButton.css';
import { isCampaignActive } from '../SponsoredListings.util';
import { getZoneIcon } from '../zoneIconUtils';
import * as styles from './CampaignTabBar.css';

export type CampaignTabBarProps = {
  event: Event;
  compact?: boolean;
};

/**
 * Renders a horizontal button-style tab bar for managing multiple campaigns per event.
 * Only rendered when SponsoredListingsV2_MultiCampaignsSupport is on — the caller
 * (EventItemHeaderV2) is responsible for the feature flag routing.
 *
 * When compact=true, renders as flat fragments (no wrapper div, no "All Listings"
 * button) so buttons can flow inline inside the caller's flex layout.
 */
export const CampaignTabBar = ({ event, compact }: CampaignTabBarProps) => {
  return (
    <EventMapContextProvider event={event} venueMapOnly>
      <CampaignTabBarInner event={event} compact={compact} />
    </EventMapContextProvider>
  );
};

const CampaignTabBarInner = ({ event, compact }: CampaignTabBarProps) => {
  const isMobile = useMatchMedia('mobile');
  const isCompact = compact ?? isMobile;
  const { setModal } = useContext(ModalContext);
  const { appContext } = useAppContext();
  const { allCampaignData, isEventsEnabledForSponsoredListingsLoading } =
    useAdCampaignsDataContext();
  const {
    eventsExpansion: { toggleListItemExpansion },
  } = useCatalogDataContext();

  const hasAdPlatformViewPermission = useUserHasAnyOfPermissions(
    Permission.AdPlatform_CampaignsViewAll
  );
  const hasAdPlatformCreatePermission =
    useUserHasAnyOfPermissions(Permission.AdPlatform_CampaignsCreate) &&
    hasAdPlatformViewPermission;
  const hasAdPlatformEditPermission =
    useUserHasAnyOfPermissions(
      Permission.AdPlatform_CampaignsEdit,
      Permission.AdPlatform_Edit
    ) && hasAdPlatformViewPermission;

  const { venue } = useGetEventFullInfo(event);
  const isSponsoredListingsEligible = getIsSponsoredListingsEligibleEvent(
    venue?.ctryCode,
    appContext?.sponsoredListingsAllowedCountryCodes
  );

  const ticketClasses = useEventMapContextTicketClassesSelector();

  const ticketClassMap = useMemo<Record<number, TicketClassInfo>>(() => {
    return ticketClasses.reduce(
      (acc, tc) => {
        if (tc?.id) {
          acc[tc.id] = tc;
        }
        return acc;
      },
      {} as Record<number, TicketClassInfo>
    );
  }, [ticketClasses]);

  const campaigns = useMemo<SponsoredListingsCampaign[]>(() => {
    if (event.viagId == null || !allCampaignData) {
      return [];
    }
    return allCampaignData[event.viagId] ?? [];
  }, [allCampaignData, event.viagId]);

  const isAllowedToBoost = useMemo(
    () =>
      (hasAdPlatformCreatePermission || hasAdPlatformEditPermission) &&
      isSponsoredListingsEligible &&
      (campaigns.length > 0 ||
        isEventsEnabledForSponsoredListingsLoading(event.viagId ?? null)),
    [
      hasAdPlatformCreatePermission,
      hasAdPlatformEditPermission,
      isSponsoredListingsEligible,
      campaigns.length,
      isEventsEnabledForSponsoredListingsLoading,
      event.viagId,
    ]
  );

  const onAllListingsClick: MouseEventHandler = (e) => {
    e.preventDefault();
    e.stopPropagation();
    toggleListItemExpansion([event.virtId]);
  };

  const openCampaignModal =
    (campaign: SponsoredListingsCampaign | null): MouseEventHandler =>
    (e) => {
      e.preventDefault();
      e.stopPropagation();

      setModal({
        children: <CampaignModal eventId={event.viagId!} campaign={campaign} />,
        clickOutsideToClose: true,
        size: 'slide-in-md',
      });
    };

  if (!isAllowedToBoost) {
    return null;
  }

  const campaignButtons = campaigns.map((campaign) => {
    const isActive = isCampaignActive(campaign);
    const targetIds = campaign.targetTicketClassIds;
    const hasZones = targetIds != null && targetIds.length > 0;

    const firstZone = hasZones ? ticketClassMap[targetIds![0]] : undefined;

    const zoneCount = targetIds?.length ?? 0;
    const zoneTooltip = hasZones
      ? zoneCount > 1
        ? `${firstZone?.name ?? 'Zone'} +${zoneCount - 1} more`
        : (firstZone?.name ?? 'Zone')
      : undefined;

    const ZoneIconComponent = getZoneIcon(firstZone?.name);

    return (
      <BoostListingTabButton
        key={campaign.campaignId}
        campaign={campaign}
        event={event}
        isActive={isActive}
        zoneIcon={ZoneIconComponent}
        zoneTooltip={zoneTooltip}
        compact={isCompact}
        onClick={openCampaignModal(campaign)}
      />
    );
  });

  const startButton = hasAdPlatformCreatePermission ? (
    <StartCampaignTabButton
      onClick={openCampaignModal(null)}
      hasCampaigns={campaigns.length > 0}
      compact={isCompact}
    />
  ) : null;

  if (isCompact) {
    return (
      <>
        {campaignButtons}
        {startButton}
      </>
    );
  }

  return (
    <div className={styles.tabBar}>
      {/* All Listings tab — toggles the event's listing expansion */}
      <Button
        variant="outlineBrand"
        shape="rect"
        className={tabButtonStyles.campaignTabButton}
        color={vars.color.textBrandV2}
        onClick={onAllListingsClick}
      >
        <Stack gap="s" alignItems="center">
          <Content id={ContentId.AllListings} />
          <ChevronDownIconV2 size={vars.iconSize.s} />
        </Stack>
      </Button>

      {campaignButtons}

      {hasAdPlatformCreatePermission && (
        <div className={styles.startButton}>{startButton}</div>
      )}
    </div>
  );
};
